import { call, put, select } from 'typed-redux-saga/macro';

import { getRoute } from 'Config';
import {
  I13N_ACTION_VERIFY,
  I13N_CATEGORY_UI,
  I13N_TYPE_FEATURE_COUNT,
} from 'Lib/constants/i13nSpec';
import { debugCaptchaLogic as debug } from 'Lib/debug';
import getI13nUtils from 'Lib/i13n/getI13nUtils';
import type { GetActionByType } from 'Lib/reduxType';
import { withErrorHandling } from 'Lib/sagaHelpers';
import { getServiceFn } from 'Services';
import { AUTH_SERVICE } from 'Services/constants';

import { captchaVerifyFailure, captchaVerifySuccess } from 'Store/captcha/actions';
import { CAPTCHA_VENDOR_ARKOSE, CAPTCHA_VERIFY_NEW_USER } from 'Store/captcha/constants';
import {
  ACTION_NAME_ERROR,
  ACTION_NAME_SUBMIT,
  ARKOSE_CHALLENGE_V2,
  STEP_ID_ERROR,
  STEP_ID_SUBMIT,
} from 'Store/captcha/tracking';
import { reauth } from 'Store/mfa/actions';
import { getAuthFunnelSessionId } from 'Store/mfa/selectors';
import { getCaptchaSessionIDFromToken } from 'Store/mfa/utils';
import { openModal } from 'Store/modals/actions';
import { MODAL_CONFIRMATION } from 'Store/modals/constants';
import { navigate } from 'Store/routing/actions';
import { getAppealChallengeToken } from 'Store/user/selectors';

import { trackEvent } from 'Store/sparks/actions';
import { CAPTCHA_CHALLENGE_RESULT, TRUST_CHALLENGE } from 'Store/sparks/sparks.events';

// Google Analytics
const GA_CAPTCHA_SUBMIT = 'Submit';
const GA_CAPTCHA_SUBMIT_SUCCESS = 'Success';
const GA_CAPTCHA_SUBMIT_FAILURE = 'Failure';

const DEFAULT_ERR_MSG = 'Captcha verification failed';

const { executeEvent } = getI13nUtils();

function* handleAccept() {
  yield* put(
    navigate(
      getRoute('login', {
        pathConfig: { withPrefix: false },
      })
    )
  );
}
function* captchaErrorHandler() {
  yield* put(captchaVerifyFailure(DEFAULT_ERR_MSG));
  yield* put(
    openModal(MODAL_CONFIRMATION, {
      props: {
        title: 'errorTitle',
        subtitles: 'ErrorPage.somethingWentWrong',
        acceptButton: 'okay',
        handleAccept,
        hideCancelButton: true,
      },
    })
  );
}

export function* _verifyCaptchaSaga(action: GetActionByType<typeof CAPTCHA_VERIFY_NEW_USER>) {
  let response, errorMessage, vendor, token, suppressed;

  const baseConfig = {
    category: I13N_CATEGORY_UI,
    action: I13N_ACTION_VERIFY,
    value: 1,
  };

  executeEvent(I13N_TYPE_FEATURE_COUNT, {
    ...baseConfig,
    label: 'CaptchaLogic',
    message: GA_CAPTCHA_SUBMIT,
  });

  try {
    if (action?.payload) {
      ({ token, vendor, suppressed } = action.payload);
      const funnelSessionId = yield* select(getAuthFunnelSessionId);
      // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
      response = yield* call(yield* call(getServiceFn, AUTH_SERVICE, 'verifyCaptcha' as const), {
        token,
        vendor,
        funnelSessionId,
      });
    }
  } catch (err: any) {
    errorMessage = err.message;
    debug(err);
  }

  const isSuccess = response?.data?.is_success ?? false;
  const sessionToken =
    token && vendor === CAPTCHA_VENDOR_ARKOSE ? getCaptchaSessionIDFromToken(token) : token;

  yield* put(
    trackEvent(CAPTCHA_CHALLENGE_RESULT, {
      challengeType: vendor,
      suppressed,
      solved: isSuccess,
      sessionToken,
    })
  );

  if (isSuccess) {
    executeEvent(I13N_TYPE_FEATURE_COUNT, {
      ...baseConfig,
      label: 'CaptchaLogic',
      message: GA_CAPTCHA_SUBMIT_SUCCESS,
    });

    yield* put(captchaVerifySuccess());
  } else {
    executeEvent(I13N_TYPE_FEATURE_COUNT, {
      ...baseConfig,
      label: 'CaptchaLogic',
      message: GA_CAPTCHA_SUBMIT_FAILURE,
    });

    yield* put(captchaVerifyFailure(errorMessage || DEFAULT_ERR_MSG));
  }
}

export const verifyCaptchaSaga = withErrorHandling(_verifyCaptchaSaga, {
  name: 'verifyCaptchaSaga',
  errorHandler: captchaErrorHandler,
});

export const captchaAppealBanSaga = withErrorHandling(
  function* (captchaResponse) {
    if (captchaResponse && captchaResponse.payload) {
      const appealToken = yield* select(getAppealChallengeToken);
      const { token: answer, vendor } = captchaResponse.payload;
      const challengeSessionId = getCaptchaSessionIDFromToken(answer);

      yield* put(
        trackEvent(TRUST_CHALLENGE, {
          actionName: ACTION_NAME_SUBMIT,
          stepId: STEP_ID_SUBMIT,
          challengeSessionId,
          challengeType: ARKOSE_CHALLENGE_V2,
        })
      );

      // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
      yield* call(
        yield* call(getServiceFn, AUTH_SERVICE, 'appealBan' as const),
        appealToken,
        answer,
        vendor
      );
      // As long as the response to appealBan is a 200, we can try to login
      yield* put(reauth());
    }
  },
  {
    name: 'captchaAppealBanSaga',
    *errorHandler() {
      yield* put(
        trackEvent(TRUST_CHALLENGE, {
          actionName: ACTION_NAME_ERROR,
          stepId: STEP_ID_ERROR,
          challengeType: ARKOSE_CHALLENGE_V2,
        })
      );
      yield* call(captchaErrorHandler);
    },
  }
);
